import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useState,
} from 'react'
import {
  FormProvider, useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Consignor from 'features/declaration/form/components/transit-parties/Consignor'
import Consignee from 'features/declaration/form/components/transit-parties/Consignee'
import { HouseConsignmentAdditionalSupplyChainActors } from 'features/declaration/form/components/AdditionalSupplyChainActors'
import { HouseConsignmentFormValues } from 'features/declaration/house-consignment/models/schema'
import DepartureTransportMeans from 'features/declaration/form/components/DepartureTransportMeans'
import SupportingDocuments from 'features/declaration/form/components/SupportingDocuments'
import TransportDocuments from 'features/declaration/form/components/TransportDocuments'
import PreviousDocuments from 'features/declaration/form/components/PreviousDocuments'
import {
  blankItemDepartureTransportMeans,
  blankSupportingDocument,
  blankTransportDocument,
  blankPreviousDocument,
} from 'features/declaration/models/blankItems'
import { HandleSubmitResult } from 'features/declaration/form/components/TransitFormTabHandle'
import useHouseConsignment from '../../../../house-consignment/components/useHouseConsignment'
import { toHouseConsignmentFormValues } from '../../../../house-consignment/models/mapper'
import TransitHouseConsignmentInfo from './TransitHouseConsignmentInfo'
import useDeclarationCountry from '../../../components/useDeclarationCountry'
import { getErrorMessage } from '../../../../../../errors/ApiError'
import {
  HouseConsignmentFormHandle,
  HouseConsignmentRowData,
} from '../../../../house-consignment/components/types'
import useHouseConsignmentForm, { HouseConsignmentFormSaveResult } from '../../../../house-consignment/components/useHouseConsignmentForm'
import PlaceOfAcceptance from '../../../../house-consignment/components/PlaceOfAcceptance'
import PlaceOfDelivery from '../../../../house-consignment/components/PlaceOfDelivery'
import CountriesOfRouting from '../../../../house-consignment/components/CountriesOfRouting'
import PassiveBorderTransportMeans from '../../../../house-consignment/components/PassiveBorderTransportMeans'
import Buyer from '../../../../house-consignment/components/Buyer'
import Seller from '../../../../house-consignment/components/Seller'
import CollapsibleFormGroup from '../../../../../../components/p5/CollapsibleFormGroup'
import { useDeclarationEditorContext } from '../../../contexts/DeclarationEditorContext'
import LoadingOverlay from '../../../../../../components/p5/LoadingOverlay'
import useScopedServerRequirements from '../../../validations/useScopedServerRequirements'
import useNctsPhase from '../../../components/useNctsPhase'
import { notifyHouseConsignmentSaveResult } from '../../../../house-consignment/components/notifyHouseConsignmentSaveResult'

interface HouseConsignmentFormProps {
  rowData: HouseConsignmentRowData
  onSaveSuccess?: () => void
}

const TransitHouseConsignmentForm = forwardRef<
HouseConsignmentFormHandle,
HouseConsignmentFormProps
>(({
  rowData,
  onSaveSuccess,
}, ref) => {
  const { declarationData, editorData } = useDeclarationEditorContext()
  const { phase, isOptIn } = useNctsPhase()
  const { consignment, transitOperation } = declarationData
  if (!transitOperation?.id) throw new Error('Missing transit operation')
  if (!consignment?.id) throw new Error('Missing consignment')

  const { t } = useTranslation()
  const [isSaving, setIsSaving] = useState(false)
  const [isFormPopulated, setIsFormPopulated] = useState(false)
  const [isCleanBaselineSet, setIsCleanBaselineSet] = useState(false)

  const { id } = rowData

  const consignmentId = consignment.id
  const transitOperationId = transitOperation.id

  const {
    houseConsignment, houseConsignmentDocuments, isLoading, saveHouseConsignmentFormValues,
  } = useHouseConsignment(id, transitOperationId, consignmentId)

  const { form } = useHouseConsignmentForm()
  const {
    reset, handleSubmit, formState: { isDirty },
  } = form

  const { isLoading: isServerRequirementsLoading } = useScopedServerRequirements('houseConsignment', {
    transitOperationId,
    phase,
    isOptIn,
    houseConsignmentId: id,
  }, { enabled: isFormPopulated })

  useEffect(() => {
    // Populate once on initial mount. Re-running this effect after a failed
    // save (via isSaving deps) used to spuriously clear isDirty even with
    // keepDirtyValues: true, breaking the drawer's save-on-close guard on
    // the second attempt. save() rebases on success; the form owns its dirty
    // state in between.
    if (isFormPopulated) return
    if (!houseConsignment || !houseConsignmentDocuments) return
    reset(toHouseConsignmentFormValues(houseConsignment, houseConsignmentDocuments))
    setIsFormPopulated(true)
  }, [reset, houseConsignment, houseConsignmentDocuments, isFormPopulated])

  useEffect(() => {
    if (isCleanBaselineSet || !isFormPopulated || isLoading || isServerRequirementsLoading) return
    reset(form.getValues())
    setIsCleanBaselineSet(true)
  }, [isCleanBaselineSet, isFormPopulated, isLoading, isServerRequirementsLoading, reset, form])

  const inlandModeOfTransport = editorData?.inlandModeOfTransport ?? ''
  const borderModeOfTransport = editorData?.borderModeOfTransport ?? ''
  const country = useDeclarationCountry()

  const [
    differentBuyer,
    differentSeller,
  ] = useWatch({
    control: form.control,
    name: [
      'differentBuyer',
      'differentSeller',
    ],
  })

  const hasSellerBuyerData = Boolean(differentBuyer) || Boolean(differentSeller)

  const validateAndGet = useCallback(() => new Promise<HandleSubmitResult<HouseConsignmentFormValues>>((resolve) => {
    handleSubmit(
      (data) => {
        resolve({ success: true, data })
      },
      (errors) => {
        resolve({ success: false, errors })
      },
    )()
  }), [handleSubmit])

  const save = useCallback(async (): Promise<HouseConsignmentFormSaveResult> => {
    setIsSaving(true)

    try {
      const result = await validateAndGet()
      if (!result.success) {
        return { success: false, errorReason: 'validation' }
      }

      if (!result.data) {
        return { success: false, errorReason: 'general', errors: [{ message: 'Data missing' }] }
      }

      const { data: saved, errors } = await saveHouseConsignmentFormValues({
        values: result.data,
        isDraft: true,
        standalone: true,
      })

      if (errors.length) {
        return { success: false, errorReason: 'general', errors }
      }

      // Rebase the form on the just-saved server state.
      form.reset(toHouseConsignmentFormValues(saved.houseConsignment, saved.houseConsignmentDocuments))

      onSaveSuccess?.()
      return { success: true, data: saved }
    } catch (err) {
      return { success: false, errorReason: 'unexpected', errors: [{ message: getErrorMessage(err) }] }
    } finally {
      setIsSaving(false)
    }
  }, [form, onSaveSuccess, saveHouseConsignmentFormValues, validateAndGet])

  useImperativeHandle(ref, () => ({
    save,
    isDirty: () => isDirty,
  }))

  const handleFormSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()
    const result = await save()
    notifyHouseConsignmentSaveResult(result, t)
  }

  return (
    <LoadingOverlay loading={isLoading || isServerRequirementsLoading || isSaving}>
      <FormProvider {...form}>
        <form onSubmit={handleFormSubmit}>
          <div className="hc-form">
            <div className="hc-form__thirds">
              <TransitHouseConsignmentInfo />
            </div>
            <CollapsibleFormGroup title={t('declaration.p5.houseConsignmentDrawer.transitParties')}>
              <div className="row">
                <div className="col-lg-4">
                  <Consignor<HouseConsignmentFormValues> scope="consignor" nctsScope="houseConsignment" />
                </div>
                <div className="col-lg-4">
                  <Consignee<HouseConsignmentFormValues> scope="consignee" nctsScope="houseConsignment" />
                </div>
                <div className="col-lg-4">
                  <HouseConsignmentAdditionalSupplyChainActors />
                </div>
              </div>
            </CollapsibleFormGroup>
            <CollapsibleFormGroup title={t('declaration.p5.houseConsignmentDrawer.documents')}>
              <div className="row">
                <div className="col-lg-4">
                  <TransportDocuments<HouseConsignmentFormValues>
                    scope="houseConsignment"
                    createDefault={() => blankTransportDocument}
                    country={country}
                    isEns={false}
                  />
                </div>
                <div className="col-lg-4">
                  <SupportingDocuments<HouseConsignmentFormValues>
                    scope="houseConsignment"
                    createDefault={() => blankSupportingDocument}
                    isEns={false}
                    country={country}
                  />
                </div>
                <div className="col-lg-4">
                  <PreviousDocuments<HouseConsignmentFormValues>
                    scope="houseConsignment"
                    createDefault={() => blankPreviousDocument}
                    country={country}
                  />
                </div>
              </div>
            </CollapsibleFormGroup>

            <CollapsibleFormGroup title={t('declaration.p5.houseConsignmentDrawer.transportation')}>
              <div className="row">
                <div className="col-lg-4">
                  <DepartureTransportMeans<HouseConsignmentFormValues>
                    name="departureTransportMeans"
                    createDefault={() => blankItemDepartureTransportMeans}
                    inlandModeOfTransport={inlandModeOfTransport}
                  />
                </div>
                <div className="col-lg-4">
                  <PlaceOfAcceptance />
                  <PlaceOfDelivery />
                </div>
                <div className="col-lg-4">
                  <CountriesOfRouting />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <PassiveBorderTransportMeans borderModeOfTransport={borderModeOfTransport} entriesGrid />
                </div>
              </div>
            </CollapsibleFormGroup>

            <CollapsibleFormGroup
              key={`sellerbuyer-${isFormPopulated}`}
              title={t('declaration.p5.houseConsignmentDrawer.sellerAndBuyer')}
              initialOpen={hasSellerBuyerData}
            >
              <div className="row">
                <div className="col-lg-4">
                  <Buyer />
                </div>
                <div className="col-lg-4">
                  <Seller />
                </div>
              </div>
            </CollapsibleFormGroup>
          </div>
        </form>
      </FormProvider>
    </LoadingOverlay>
  )
})

TransitHouseConsignmentForm.defaultProps = {
  onSaveSuccess: undefined,
}

export default TransitHouseConsignmentForm
